/**
 * HTTP client for personalized distribution (routes in distributions.types.ts). Each function
 * returns the wire shape unchanged; mappers shape it for the UI. REACT_APP_DISTRIBUTIONS_API=mock
 * answers from distributionsApi.mock.ts instead, with the same 202-then-poll behaviour.
 */
import { isMockApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService } from "../api";
import { newIdempotencyKey, writeOptions } from "./distributionIdempotency";
import * as mock from "./distributionsApi.mock";
import {
  LIMITS,
  type AudienceApi,
  type DistributionApi,
  type DistributionCreateApi,
  type DistributionThemeApi,
  type DistributionThemeListApi,
  type DistributionListApi,
  type DistributionPatchApi,
  type DistributionScheduleApi,
  type DistributionStatusRequestApi,
  type EditStatusApi,
  type FormatApi,
  type PreviewRenderApi,
  type PreviewRenderRequestApi,
  type RecipientPageApi,
  type RecipientsPutApi,
  type RetryAcceptedApi,
  type RunAcceptedApi,
  type RunDetailApi,
  type RunListFiltersApi,
  type RunPageApi,
  type RunRequestApi,
  type SchedulePreviewApi,
  type SchedulePreviewRequestApi,
  type SignedFileApi,
  type SliceDetailApi,
  type SplitCheckApi,
  type SplitCheckRequestApi,
  type SplitFieldListApi,
  type ValueStatsApi,
  type ValueStatsRequestApi,
  type WriteAcceptedApi,
} from "./distributions.types";

export const isMockDistributionsApi = (): boolean => isMockApiFlag(process.env.REACT_APP_DISTRIBUTIONS_API);

const E = API_ENDPOINTS.distributions;

const withPage = (path: string, offset: number, limit: number): string => `${path}?offset=${offset}&limit=${limit}`;

/** `scope` "archived" lists only the archived ones; the default leaves them out. */
export type DistributionListScope = "current" | "archived";

/** A server that predates `?status=archived` ignores it and returns the live list, so keep only archived rows. */
const archivedOnly = (list: DistributionListApi): DistributionListApi => ({ ...list, items: list.items.filter((d) => d.status === "archived") });

export const listDistributions = (storyId: string, scope: DistributionListScope = "current"): Promise<DistributionListApi> =>
  isMockDistributionsApi()
    ? mock.mockListDistributions(storyId, scope)
    : scope === "archived"
      ? apiService.get<DistributionListApi>(`${E.list(storyId)}?status=archived`).then(archivedOnly)
      : apiService.get<DistributionListApi>(E.list(storyId));

export const createDistribution = (storyId: string, body: DistributionCreateApi, idempotencyKey = newIdempotencyKey()): Promise<WriteAcceptedApi> =>
  isMockDistributionsApi() ? mock.mockCreateDistribution(storyId, body) : apiService.post<WriteAcceptedApi>(E.list(storyId), body, writeOptions(idempotencyKey));

export const getDistributionEdit = (storyId: string, requestId: string): Promise<EditStatusApi> =>
  isMockDistributionsApi() ? mock.mockGetEditStatus(storyId, requestId) : apiService.get<EditStatusApi>(E.edit(storyId, requestId));

export const checkSplit = (storyId: string, body: SplitCheckRequestApi): Promise<SplitCheckApi> =>
  isMockDistributionsApi() ? mock.mockSplitCheckRoute(storyId, body) : apiService.post<SplitCheckApi>(E.splitCheck(storyId), body);

export const requestValueStats = (storyId: string, body: ValueStatsRequestApi, idempotencyKey = newIdempotencyKey()): Promise<{ request_id: string }> =>
  isMockDistributionsApi()
    ? mock.mockRequestValueStats(storyId, body)
    : apiService.post<{ request_id: string }>(E.valueStats(storyId), body, writeOptions(idempotencyKey));

export const getValueStats = (storyId: string, requestId: string): Promise<ValueStatsApi> =>
  isMockDistributionsApi() ? mock.mockGetValueStats(storyId, requestId) : apiService.get<ValueStatsApi>(E.valueStatsResult(storyId, requestId));

export const previewDistributionSchedule = (storyId: string, body: SchedulePreviewRequestApi): Promise<SchedulePreviewApi> =>
  isMockDistributionsApi() ? mock.mockSchedulePreview(storyId, body) : apiService.post<SchedulePreviewApi>(E.schedulePreview(storyId), body);

export const getDistribution = (storyId: string, distributionId: string): Promise<DistributionApi> =>
  isMockDistributionsApi() ? mock.mockGetDistribution(storyId, distributionId) : apiService.get<DistributionApi>(E.detail(storyId, distributionId));

export const patchDistribution = (storyId: string, distributionId: string, body: DistributionPatchApi, idempotencyKey = newIdempotencyKey()): Promise<WriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockPatchDistribution(storyId, distributionId, body)
    : apiService.patch<WriteAcceptedApi>(E.detail(storyId, distributionId), body, writeOptions(idempotencyKey));

export const deleteDistribution = (storyId: string, distributionId: string, idempotencyKey = newIdempotencyKey()): Promise<WriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockDeleteDistribution(storyId, distributionId)
    : apiService.delete<WriteAcceptedApi>(E.detail(storyId, distributionId), writeOptions(idempotencyKey));

export const setDistributionStatus = (storyId: string, distributionId: string, body: DistributionStatusRequestApi, idempotencyKey = newIdempotencyKey()): Promise<WriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockSetDistributionStatus(storyId, distributionId, body)
    : apiService.post<WriteAcceptedApi>(E.status(storyId, distributionId), body, writeOptions(idempotencyKey));

export const restoreDistribution = (storyId: string, distributionId: string, idempotencyKey = newIdempotencyKey()): Promise<WriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockRestoreDistribution(storyId, distributionId)
    : apiService.post<WriteAcceptedApi>(E.restore(storyId, distributionId), {}, writeOptions(idempotencyKey));

/** `null` = send on demand only. The body is JSON `null` then, which the contract allows. */
export const putDistributionSchedule = (storyId: string, distributionId: string, schedule: DistributionScheduleApi | null, idempotencyKey = newIdempotencyKey()): Promise<WriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockPutSchedule(storyId, distributionId, schedule)
    : apiService.put<WriteAcceptedApi>(E.schedule(storyId, distributionId), schedule as object, undefined, writeOptions(idempotencyKey));

export const getRecipients = (storyId: string, distributionId: string, offset = 0, limit: number = LIMITS.maxRecipients): Promise<RecipientPageApi> =>
  isMockDistributionsApi()
    ? mock.mockGetRecipients(storyId, distributionId, offset, limit)
    : apiService.get<RecipientPageApi>(withPage(E.recipients(storyId, distributionId), offset, limit));

export const putRecipients = (storyId: string, distributionId: string, body: RecipientsPutApi, idempotencyKey = newIdempotencyKey()): Promise<WriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockPutRecipients(storyId, distributionId, body)
    : apiService.put<WriteAcceptedApi>(E.recipients(storyId, distributionId), body, undefined, writeOptions(idempotencyKey));

/** Proposed route (see the handoff doc): re-runs the Slack checks on the stored rows. */
export const recheckRecipients = (storyId: string, distributionId: string, idempotencyKey = newIdempotencyKey()): Promise<WriteAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockRecheckRecipients(storyId, distributionId)
    : apiService.post<WriteAcceptedApi>(E.recipientsRecheck(storyId, distributionId), {}, writeOptions(idempotencyKey));

export const startDistributionRun = (storyId: string, distributionId: string, body: RunRequestApi, idempotencyKey = newIdempotencyKey()): Promise<RunAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockStartRun(storyId, distributionId, body)
    : apiService.post<RunAcceptedApi>(E.runs(storyId, distributionId), body, writeOptions(idempotencyKey));

/** Sends again only the deliveries of a run that didn't arrive. 409 `RUN_IN_PROGRESS` / `NOTHING_TO_RETRY` carry their reason. */
export const retryFailedDeliveries = (storyId: string, distributionId: string, runId: string, idempotencyKey = newIdempotencyKey()): Promise<RetryAcceptedApi> =>
  isMockDistributionsApi()
    ? mock.mockRetryFailed(storyId, distributionId, runId)
    : apiService.post<RetryAcceptedApi>(E.runRetryFailed(storyId, distributionId, runId), {}, writeOptions(idempotencyKey));

/** Round 6 (WP3): `?trigger=now&trigger=test&status=failed`, each repeatable (addendum §3.4). */
export const runFilterQuery = (filters: RunListFiltersApi = {}): string =>
  [...(filters.trigger ?? []).map((t) => `trigger=${encodeURIComponent(t)}`), ...(filters.status ?? []).map((st) => `status=${encodeURIComponent(st)}`)]
    .map((part) => `&${part}`)
    .join("");

export const listDistributionRuns = (
  storyId: string,
  distributionId: string,
  offset = 0,
  limit = 20,
  filters: RunListFiltersApi = {},
): Promise<RunPageApi> =>
  isMockDistributionsApi()
    ? mock.mockListRuns(storyId, distributionId, offset, limit, filters)
    : apiService.get<RunPageApi>(`${withPage(E.runs(storyId, distributionId), offset, limit)}${runFilterQuery(filters)}`);

export const getDistributionRun = (storyId: string, distributionId: string, runId: string): Promise<RunDetailApi> =>
  isMockDistributionsApi() ? mock.mockGetRun(storyId, distributionId, runId) : apiService.get<RunDetailApi>(E.run(storyId, distributionId, runId));

export const getSliceDetail = (storyId: string, distributionId: string, runId: string, sliceId: string): Promise<SliceDetailApi> =>
  isMockDistributionsApi()
    ? mock.mockGetSlice(storyId, distributionId, runId, sliceId)
    : apiService.get<SliceDetailApi>(E.slice(storyId, distributionId, runId, sliceId));

/**
 * How a signed file opens: `attachment` downloads it (the default, unchanged); `inline` lets the
 * browser show it in place, e.g. a PDF in an `<iframe>` (pd7 CONTRACT §2.3; PowerPoint stays an
 * attachment whatever is asked).
 */
export type FileDispositionApi = "attachment" | "inline";

export const getSliceFile = (
  storyId: string,
  distributionId: string,
  runId: string,
  sliceId: string,
  format: FormatApi,
  disposition: FileDispositionApi = "attachment",
): Promise<SignedFileApi> => {
  if (isMockDistributionsApi()) return mock.mockGetSliceFile(storyId, distributionId, runId, sliceId, format);
  const path = E.sliceFile(storyId, distributionId, runId, sliceId, format);
  return disposition === "inline" ? apiService.get<SignedFileApi>(path, { disposition }) : apiService.get<SignedFileApi>(path);
};

/** Which files the zip of a run holds. */
export type RunZipFormatApi = "all" | FormatApi;

/** A zip of every file of a run, with the name the server gave it. */
export interface RunZipApi {
  blob: Blob;
  filename: string;
}

/**
 * Downloads every file of a run as one zip (the Story owner only). The route needs the sign-in
 * token, so it comes as a blob. A run with no files is 404; a run with too many is 413 and the
 * server's own message says what to do (read it with getApiErrorMessage).
 */
export const getRunFilesZip = async (
  storyId: string,
  distributionId: string,
  runId: string,
  format: RunZipFormatApi = "all",
): Promise<RunZipApi> => {
  if (isMockDistributionsApi()) return mock.mockGetRunFilesZip(storyId, distributionId, runId, format);
  const { blob, filename } = await apiService.getBlobWithName(E.runFilesZip(storyId, distributionId, runId), { format });
  return { blob, filename: filename ?? "files.zip" };
};

/**
 * Fields the story can be split by, with their descriptions, where they read from and whether
 * `audience` may see their values, plus the class each audience's copies are redacted at
 * (`clearances`, for the caller).
 */
export const listSplitFields = async (storyId: string, audience: AudienceApi): Promise<SplitFieldListApi> =>
  isMockDistributionsApi()
    ? mock.mockListSplitFields(storyId, audience)
    : apiService.get<SplitFieldListApi>(E.splitFields(storyId), { audience });

/** Deck themes a copy can be drawn in. A theme's `id` is what a write sends back as `theme_id`. */
export const listDistributionThemes = async (): Promise<DistributionThemeApi[]> => {
  if (isMockDistributionsApi()) return mock.mockListDistributionThemes();
  const response = await apiService.get<DistributionThemeListApi>(API_ENDPOINTS.distributionThemes.list);
  return response.items;
};

/** Builds one copy without a run. The same key continues or reuses the finished render; `force` starts a new one. */
export const startPreviewRender = (storyId: string, distributionId: string, body: PreviewRenderRequestApi): Promise<PreviewRenderApi> =>
  isMockDistributionsApi() ? mock.mockStartPreviewRender(storyId, distributionId, body) : apiService.post<PreviewRenderApi>(E.previewRender(storyId, distributionId), body);

/** Re-asks for a render that was still running; waits on the server up to `waitMs`. */
export const getPreviewRender = (storyId: string, distributionId: string, renderId: string, waitMs = 8000): Promise<PreviewRenderApi> =>
  isMockDistributionsApi()
    ? mock.mockGetPreviewRender(storyId, distributionId, renderId)
    : apiService.get<PreviewRenderApi>(`${E.previewRenderGet(storyId, distributionId, renderId)}?wait_ms=${waitMs}`);
